import { useLayoutEffect, useMemo, useRef } from 'react';
import { typography, radius } from '@/shared/theme/tokens';
import { useFurnaceSvg } from '../../hooks/useFurnaceSvg';
import { Sparkline } from '../../ui/Sparkline';
import { LIVE_STATUS_META } from '../../liveModeMeta';
import { BCAST } from './broadcastMeta';

const STATUS_RANK = { critical: 0, warning: 1, ok: 2 };

// One callout per parameter, worst-status first; stale params are never floated
// as a live reading. Kept to two — this is a backdrop, not the headline.
function pickCallouts(params, max = 2) {
  const byName = new Map();
  for (const p of params) {
    if (p.isStale) continue;
    const cur = byName.get(p.parameterName);
    if (!cur || STATUS_RANK[p.status] < STATUS_RANK[cur.status]) byName.set(p.parameterName, p);
  }
  return [...byName.values()]
    .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status])
    .slice(0, max);
}

/**
 * The studio backdrop — the plant SVG rendered as a wireframe "set" behind the
 * anchor desk, with a couple of live monitor chips. Deliberately a SUPPORTING
 * panel (identity + ambience), never the hero: the recommendation lower-third
 * and the breaking bar own the pixels.
 */
export function StudioBackdrop({ svgUrl, params = [] }) {
  const hostRef = useRef(null);
  const { data: markup, isLoading, isError } = useFurnaceSvg(svgUrl);
  const callouts = useMemo(() => pickCallouts(params), [params]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !markup) return;
    host.innerHTML = markup;
    const svg = host.querySelector('svg');
    if (svg) {
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.style.display = 'block';
      svg.style.maxHeight = '100%';
    }
  }, [markup]);

  return (
    <div style={{
      flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
      background: BCAST.panel, border: `1px solid ${BCAST.line}`, borderRadius: radius.sm, overflow: 'hidden',
    }}>
      {/* Studio strap */}
      <div style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px',
        background: BCAST.chrome, borderBottom: `1px solid ${BCAST.line}`,
      }}>
        <span style={{ width: 3, height: 11, background: BCAST.brand, flexShrink: 0 }} />
        <span style={{
          fontFamily: BCAST.heading, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
          letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', color: BCAST.inkDim,
        }}>
          Studio Feed
        </span>
        <span style={{
          fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, color: BCAST.inkFaint,
          letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
        }}>
          Plant Set
        </span>
      </div>

      {/* Backdrop */}
      <div style={{ position: 'relative', flex: 1, minHeight: 0, background: BCAST.studio, overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 14, opacity: 0.78,
          // Cool steel-cyan wireframe ghost of the plant on the studio void.
          filter: 'brightness(0) invert(0.6) sepia(0.55) saturate(2.6) hue-rotate(150deg) brightness(1.04)',
        }}>
          {isLoading && (
            <span style={{ fontFamily: BCAST.mono, fontSize: typography.fontSize.sm, color: BCAST.inkFaint }}>LOADING SET…</span>
          )}
          {isError && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 30, color: BCAST.inkFaint }}>broken_image</span>
              <span style={{ fontFamily: BCAST.mono, fontSize: typography.fontSize.sm, color: BCAST.inkFaint }}>SET UNAVAILABLE</span>
            </div>
          )}
          {!isLoading && !isError && (
            <div ref={hostRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
          )}
        </div>

        {/* Live monitor chips */}
        <div style={{
          position: 'absolute', left: 12, right: 12, bottom: 12, zIndex: 2,
          display: 'flex', gap: 8, pointerEvents: 'none', flexWrap: 'wrap',
        }}>
          {callouts.map((p) => {
            const meta = LIVE_STATUS_META[p.status] ?? LIVE_STATUS_META.ok;
            return (
              <div key={p.id} style={{
                flex: '1 1 0', minWidth: 0,
                background: BCAST.panel, border: `1px solid ${meta.border}`, borderRadius: radius.xs, padding: '7px 10px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 7, height: 7, borderRadius: radius.full, background: meta.color, flexShrink: 0 }} />
                  <span style={{
                    fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, color: BCAST.inkDim,
                    letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {p.parameterName}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 3 }}>
                  <span style={{
                    fontFamily: BCAST.mono, fontSize: typography.fontSize['4xl'], fontWeight: typography.fontWeight.bold,
                    color: meta.color, fontVariantNumeric: 'tabular-nums',
                  }}>
                    {p.liveValue}
                  </span>
                  {p.unit && <span style={{ fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, color: BCAST.inkFaint }}>{p.unit}</span>}
                </div>
                <div style={{ marginTop: 3 }}>
                  <Sparkline data={p.buffer} color={meta.color} height={16} animate={false} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
